import React, { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import { FileText, Download, X, Loader2 } from 'lucide-react'
import { getMessageAttachmentSignedUrl, type MessageAttachment } from '@/lib/messageAttachments'

/**
 * Renders a message's attachments (file-sharing CP2 — render side).
 *
 * Images → a thumbnail that opens a lightbox; other files → a chip that
 * downloads on click. Private bucket, so every URL is a short-lived signed URL
 * fetched on demand and cached in React Query (staleTime just under the TTL, so
 * a rendered URL is never near expiry). Mirrors the JobPhotoStrip signed-URL
 * discipline but points at getMessageAttachmentSignedUrl (JobPhotoStrip is
 * hardcoded to the job-photos bucket, so it can't be reused directly here).
 */

function useSignedAttachmentUrl(path: string, enabled = true) {
  return useQuery({
    queryKey: ['msg-attachment-url', path],
    enabled,
    queryFn: async () => {
      const { url, error } = await getMessageAttachmentSignedUrl(path, 300)
      if (error || !url) throw error ?? new Error('No signed URL')
      return url
    },
    staleTime: 290_000,
    gcTime: 290_000,
    retry: 1,
  })
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function ImageThumb({ att, onOpen }: { att: MessageAttachment; onOpen: () => void }) {
  const { data: url, isLoading } = useSignedAttachmentUrl(att.path)
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`Open ${att.name}`}
      style={{
        width: 160, height: 120, padding: 0, background: 'var(--hover-bg)',
        border: '1px solid var(--border-subtle)', borderRadius: 10, cursor: 'pointer',
        overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      {url && !isLoading ? (
        <img src={url} alt={att.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <Loader2 style={{ width: 16, height: 16, color: 'var(--ink-tertiary)' }} className="animate-spin" />
      )}
    </button>
  )
}

function FileChip({ att }: { att: MessageAttachment }) {
  const [fetching, setFetching] = useState(false)
  const handleDownload = async () => {
    setFetching(true)
    const { url, error } = await getMessageAttachmentSignedUrl(att.path, 300)
    setFetching(false)
    if (error || !url) return
    window.open(url, '_blank', 'noopener')
  }
  return (
    <button
      type="button"
      onClick={handleDownload}
      title={`Download ${att.name}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8, maxWidth: 280,
        padding: '8px 12px', background: 'var(--surface)', border: '1px solid var(--border-default)',
        borderRadius: 10, cursor: 'pointer', textAlign: 'left',
      }}
    >
      {fetching
        ? <Loader2 style={{ width: 16, height: 16, color: 'var(--ink-tertiary)', flexShrink: 0 }} className="animate-spin" />
        : <FileText style={{ width: 16, height: 16, color: 'var(--ink-tertiary)', flexShrink: 0 }} />}
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)' }}>{att.name}</span>
      <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', flexShrink: 0 }}>{formatSize(att.size)}</span>
      <Download style={{ width: 14, height: 14, color: 'var(--ink-tertiary)', flexShrink: 0 }} />
    </button>
  )
}

function Lightbox({ att, onClose }: { att: MessageAttachment; onClose: () => void }) {
  const { data: url } = useSignedAttachmentUrl(att.path)
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 32 }}
    >
      <button onClick={onClose} aria-label="Close" style={{ position: 'absolute', top: 20, right: 20, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-inverse, var(--ink-primary))', padding: 6, display: 'flex' }}>
        <X style={{ width: 24, height: 24 }} />
      </button>
      {url && (
        <img
          src={url}
          alt={att.name}
          onClick={e => e.stopPropagation()}
          style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: 10 }}
        />
      )}
    </motion.div>
  )
}

export default function MessageAttachmentList({ attachments }: { attachments: MessageAttachment[] }) {
  const [lightbox, setLightbox] = useState<MessageAttachment | null>(null)
  if (!attachments || attachments.length === 0) return null
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
      {attachments.map((att, i) =>
        att.kind === 'image'
          ? <ImageThumb key={att.path || i} att={att} onOpen={() => setLightbox(att)} />
          : <FileChip key={att.path || i} att={att} />,
      )}
      <AnimatePresence>
        {lightbox && <Lightbox att={lightbox} onClose={() => setLightbox(null)} />}
      </AnimatePresence>
    </div>
  )
}
